import React, { useEffect, useState } from 'react';
import { openInAppFilePreview } from '../../utils/filePreviewBus';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  CircularProgress, Alert, Chip, TextField, InputAdornment, IconButton, 
  Tooltip, useMediaQuery, Button, Dialog, DialogTitle, DialogContent, 
  DialogActions, MenuItem, Avatar, TableContainer, Stack, Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import Swal from 'sweetalert2';
import { useSelector } from 'react-redux';
import { 
  getAllRab, 
  getRabForSupplier, 
  getRabForTfl, 
  upsertRab,
  deleteRab,
  getRabById
} from '../API/bantuan';
import { getBantuan } from '../API/bantuan';

// ============================================================
// KONSTANTA
// ============================================================
const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

// ============================================================
// KOMPONEN PREVIEW FILE (PDF / GAMBAR)
// ============================================================
const FilePreview = ({ filePath, fileName }) => {
  const [open, setOpen] = useState(false);
  const [fileType, setFileType] = useState('');
  const [error, setError] = useState(false);

  useEffect(() => {
    if (filePath) {
      const ext = filePath.split('.').pop().toLowerCase();
      setFileType(ext);
    }
  }, [filePath]);

  // Fungsi untuk mendapatkan URL file yang benar
  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    
    // Ambil hanya nama file dari path
    const filename = path.split('/').pop();
    
    // Gunakan endpoint /rab/file/:filename
    return `${BASE_URL}/rab/file/${filename}`;
  };

  const handlePreview = () => {
    if (!filePath) return;
    const fullUrl = getFileUrl(filePath);
    console.log('File URL:', fullUrl); // Debugging
    
    if (fileType === 'pdf') {
      setOpen(true);
      setError(false);
    } else {
      openInAppFilePreview({ url: fullUrl });
    }
  };

  const handleDownload = () => {
    if (!filePath) return;
    const fullUrl = getFileUrl(filePath);
    openInAppFilePreview({ url: fullUrl });
  };

  if (!filePath) {
    return <Typography variant="caption" color="text.secondary">Tidak ada file</Typography>;
  }

  // Untuk gambar, tampilkan thumbnail
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(fileType);
  const isPdf = fileType === 'pdf';

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {isPdf ? (
          <PictureAsPdfIcon color="error" fontSize="small" />
        ) : isImage ? (
          <ImageIcon color="primary" fontSize="small" />
        ) : (
          <DescriptionIcon color="action" fontSize="small" />
        )}
        
        <Button
          size="small"
          onClick={handlePreview}
          startIcon={isPdf ? <DescriptionIcon /> : isImage ? <ImageIcon /> : <DownloadIcon />}
          variant="outlined"
          sx={{ textTransform: 'none', fontSize: '0.65rem', py: 0.5 }}
        >
          {isPdf ? 'Preview' : isImage ? 'Lihat' : 'Buka'}
        </Button>
        
        <Tooltip title="Download File">
          <IconButton size="small" onClick={handleDownload} color="primary">
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Dialog Preview PDF */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Preview - {fileName || 'File'}</Typography>
          <IconButton onClick={() => setOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ height: '80vh', p: 0 }}>
          {error ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Alert severity="error">
                Gagal memuat file. 
                <Button size="small" onClick={handleDownload} sx={{ ml: 1 }}>
                  Download File
                </Button>
              </Alert>
            </Box>
          ) : (
            <iframe
              src={getFileUrl(filePath)}
              width="100%"
              height="100%"
              title="PDF Preview"
              style={{ border: 'none' }}
              onError={() => setError(true)}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Tutup</Button>
          <Button variant="contained" onClick={handleDownload} startIcon={<DownloadIcon />}>
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// ============================================================
// KOMPONEN DETAIL RAB
// ============================================================
const RabDetailDialog = ({ open, onClose, rab, userRole, onEdit }) => {
  if (!rab) return null;
  
  const b = rab.Bantuan || {};
  const pemohon = b.namaLengkap || b.pemohon?.nama || `Bantuan #${b.id}`;
  const isAdmin = userRole === 'admin';
  const isTFL = userRole === 'TFL';
  const canEdit = isAdmin || isTFL;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h6">Detail RAB</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">ID RAB</Typography>
            <Typography variant="body1" fontWeight="bold">#{rab.id}</Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Penerima Bantuan</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}>
                <PersonIcon />
              </Avatar>
              <Typography variant="body1">{pemohon}</Typography>
            </Box>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Jenis Bantuan</Typography>
            <Chip label={b.JenisBantuan || '-'} color="primary" size="small" sx={{ mt: 0.5 }} />
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Toko / Supplier</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <StoreIcon color="action" />
              <Typography variant="body1">
                {rab.SupplierBarang?.namaToko || rab.SupplierBarang?.namaSupplier || '-'}
              </Typography>
            </Box>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">TFL</Typography>
            <Typography variant="body1">{rab.TFL?.username || '-'}</Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Status</Typography>
            <Chip
              label={rab.status || 'draft'}
              color={rab.status === 'disetujui' ? 'success' : rab.status === 'diajukan' ? 'warning' : 'default'}
              sx={{ mt: 0.5 }}
            />
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Keterangan</Typography>
            <Typography variant="body1">{rab.keterangan || '-'}</Typography>
          </Box>
          
          <Divider />
          
          <Box>
            <Typography variant="caption" color="text.secondary">File RAB</Typography>
            <Box sx={{ mt: 1 }}>
              <FilePreview 
                filePath={rab.fileRab} 
                fileName={rab.fileRab?.split('/').pop() || ''} 
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Tutup</Button>
        {canEdit && (
          <Button 
            variant="contained" 
            startIcon={<EditIcon />}
            onClick={() => {
              onClose();
              if (onEdit) onEdit(rab);
            }}
          >
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ============================================================
// KOMPONEN UTAMA RAB MENU
// ============================================================
const RabMenu = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');

  const user = useSelector((s) => s.authAdmin?.user);
  const userRole = user?.role || localStorage.getItem('role') || '';
  const userId = user?.id || null;

  const isAdmin = userRole === 'admin';
  const isTFL = userRole === 'TFL';
  const isSupplier = userRole === 'Supplier Penyedia';
  const canUpload = isAdmin || isTFL;
  const canDelete = isAdmin;

  // Upload dialog state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [bantuanOpts, setBantuanOpts] = useState([]);
  const [supplierOpts, setSupplierOpts] = useState([]);
  const [selectedBantuan, setSelectedBantuan] = useState(null);
  const [uploadForm, setUploadForm] = useState({ 
    BantuanId: '', 
    SupplierBarangId: '',
    keterangan: '', 
    status: 'diajukan' 
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Detail dialog state
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedRab, setSelectedRab] = useState(null);

  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    BantuanId: '',
    SupplierBarangId: '',
    keterangan: '',
    status: ''
  });
  const [editFile, setEditFile] = useState(null);
  const [editBantuanOpts, setEditBantuanOpts] = useState([]);
  const [editSupplierOpts, setEditSupplierOpts] = useState([]);

  // ============================================================
  // FETCH DATA
  // ============================================================
  const fetchRows = async () => {
    try {
      setLoading(true);
      const res = await getAllRab().catch(() => null);
      if (res?.data?.data) {
        setRows(res.data.data);
        return;
      }
      const role = localStorage.getItem('role') || userRole;
      if (role === 'Supplier Penyedia') {
        const supRes = await getRabForSupplier();
        setRows(supRes.data?.data || []);
      } else {
        const tflRes = await getRabForTfl();
        setRows(tflRes.data?.data || []);
      }
    } catch (e) {
      if (e.response?.status !== 404 && !e.message?.includes('not found')) {
        setError('Gagal memuat data RAB');
      }
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  // ============================================================
  // UPLOAD / CREATE
  // ============================================================
  const openUpload = async () => {
    setUploadForm({ 
      BantuanId: '', 
      SupplierBarangId: '',
      keterangan: '', 
      status: 'diajukan' 
    });
    setUploadFile(null);
    setSelectedBantuan(null);
    setSupplierOpts([]);
    setEditingId(null);
    setUploadOpen(true);
    try {
      const res = await getBantuan({ page: 1, limit: 200 });
      const list = (res.data.data || []).map(b => {
        let supplierBarang = [];
        if (b.SupplierBarang && b.SupplierBarang.length > 0) {
          supplierBarang = b.SupplierBarang;
        } else if (b.supplierBarang && b.supplierBarang.length > 0) {
          supplierBarang = b.supplierBarang;
        } else if (b.pemohon?.supplierBarang && b.pemohon.supplierBarang.length > 0) {
          supplierBarang = b.pemohon.supplierBarang;
        } else if (b.suppliers && b.suppliers.length > 0) {
          supplierBarang = b.suppliers;
        }
        
        return {
          id: b.id,
          label: `#${b.id} - ${b.pemohon?.nama || b.namaLengkap || '-'} (${b.JenisBantuan || '-'})`,
          bantuan: {
            ...b,
            supplierBarang: supplierBarang
          }
        };
      });
      setBantuanOpts(list);
    } catch (e) { 
      console.error('Error loading bantuan:', e);
    }
  };

  const handleBantuanChange = (e) => {
    const bantuanId = e.target.value;
    const selected = bantuanOpts.find(b => b.id === parseInt(bantuanId));
    
    setSelectedBantuan(selected?.bantuan || null);
    
    let suppliers = [];
    if (selected?.bantuan?.supplierBarang && selected.bantuan.supplierBarang.length > 0) {
      suppliers = selected.bantuan.supplierBarang.map(s => ({
        id: s.id,
        label: s.namaToko || s.namaSupplier || s.nama || '-'
      }));
    } else if (selected?.bantuan?.SupplierBarang && selected.bantuan.SupplierBarang.length > 0) {
      suppliers = selected.bantuan.SupplierBarang.map(s => ({
        id: s.id,
        label: s.namaToko || s.namaSupplier || s.nama || '-'
      }));
    }
    
    setSupplierOpts(suppliers);
    
    setUploadForm((p) => ({ 
      ...p, 
      BantuanId: bantuanId,
      SupplierBarangId: suppliers.length === 1 ? suppliers[0].id : ''
    }));
  };

  const handleUploadRab = async () => {
    if (!uploadForm.BantuanId) {
      Swal.fire('Pilih bantuan dulu', '', 'warning');
      return;
    }
    if (!uploadFile) {
      Swal.fire('Pilih file RAB dulu', '', 'warning');
      return;
    }
    setSaving(true);
    try {
      await upsertRab({
        BantuanId: uploadForm.BantuanId,
        SupplierBarangId: uploadForm.SupplierBarangId || undefined,
        keterangan: uploadForm.keterangan,
        status: uploadForm.status
      }, uploadFile);
      Swal.fire('RAB tersimpan', '', 'success');
      setUploadOpen(false);
      await fetchRows();
    } catch (e) {
      Swal.fire(e.response?.data?.message || 'Gagal upload RAB', '', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // DELETE
  // ============================================================
  const handleDelete = async (id, nama) => {
    const confirm = await Swal.fire({
      title: 'Hapus RAB?',
      text: `Yakin ingin menghapus RAB untuk ${nama}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Ya, Hapus',
      cancelButtonText: 'Batal'
    });
    
    if (confirm.isConfirmed) {
      try {
        await deleteRab(id);
        Swal.fire('Berhasil dihapus', '', 'success');
        await fetchRows();
      } catch (e) {
        Swal.fire(e.response?.data?.message || 'Gagal menghapus RAB', '', 'error');
      }
    }
  };

  // ============================================================
  // VIEW DETAIL
  // ============================================================
  const handleViewDetail = (rab) => {
    setSelectedRab(rab);
    setDetailOpen(true);
  };

  // ============================================================
  // EDIT
  // ============================================================
  const openEdit = async (rab) => {
    setEditingId(rab.id);
    setEditForm({
      id: rab.id,
      BantuanId: rab.BantuanId || '',
      SupplierBarangId: rab.SupplierBarangId || '',
      keterangan: rab.keterangan || '',
      status: rab.status || 'draft'
    });
    setEditFile(null);
    setEditOpen(true);
    
    try {
      const res = await getBantuan({ page: 1, limit: 200 });
      const list = (res.data.data || []).map(b => {
        let supplierBarang = [];
        if (b.SupplierBarang && b.SupplierBarang.length > 0) {
          supplierBarang = b.SupplierBarang;
        } else if (b.supplierBarang && b.supplierBarang.length > 0) {
          supplierBarang = b.supplierBarang;
        } else if (b.pemohon?.supplierBarang && b.pemohon.supplierBarang.length > 0) {
          supplierBarang = b.pemohon.supplierBarang;
        } else if (b.suppliers && b.suppliers.length > 0) {
          supplierBarang = b.suppliers;
        }
        
        return {
          id: b.id,
          label: `#${b.id} - ${b.pemohon?.nama || b.namaLengkap || '-'} (${b.JenisBantuan || '-'})`,
          bantuan: {
            ...b,
            supplierBarang: supplierBarang
          }
        };
      });
      setEditBantuanOpts(list);
      
      if (rab.BantuanId) {
        const selected = list.find(b => b.id === rab.BantuanId);
        if (selected?.bantuan?.supplierBarang) {
          const suppliers = selected.bantuan.supplierBarang.map(s => ({
            id: s.id,
            label: s.namaToko || s.namaSupplier || s.nama || '-'
          }));
          setEditSupplierOpts(suppliers);
        }
      }
    } catch (e) { 
      console.error('Error loading bantuan for edit:', e);
    }
  };

  const handleEditBantuanChange = (e) => {
    const bantuanId = e.target.value;
    const selected = editBantuanOpts.find(b => b.id === parseInt(bantuanId));
    
    let suppliers = [];
    if (selected?.bantuan?.supplierBarang && selected.bantuan.supplierBarang.length > 0) {
      suppliers = selected.bantuan.supplierBarang.map(s => ({
        id: s.id,
        label: s.namaToko || s.namaSupplier || s.nama || '-'
      }));
    }
    
    setEditSupplierOpts(suppliers);
    
    setEditForm((p) => ({ 
      ...p, 
      BantuanId: bantuanId,
      SupplierBarangId: suppliers.length === 1 ? suppliers[0].id : ''
    }));
  };

  const handleEditSubmit = async () => {
    if (!editForm.BantuanId) {
      Swal.fire('Pilih bantuan dulu', '', 'warning');
      return;
    }
    setSaving(true);
    try {
      await upsertRab({
        BantuanId: editForm.BantuanId,
        SupplierBarangId: editForm.SupplierBarangId || undefined,
        keterangan: editForm.keterangan,
        status: editForm.status
      }, editFile);
      Swal.fire('RAB diperbarui', '', 'success');
      setEditOpen(false);
      await fetchRows();
    } catch (e) {
      Swal.fire(e.response?.data?.message || 'Gagal update RAB', '', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ============================================================
  // FILTER & SEARCH
  // ============================================================
  const filteredRows = rows.filter(r => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (r.Bantuan?.namaLengkap || '').toLowerCase().includes(term) ||
      (r.Bantuan?.pemohon?.nama || '').toLowerCase().includes(term) ||
      (r.TFL?.username || '').toLowerCase().includes(term) ||
      (r.keterangan || '').toLowerCase().includes(term) ||
      (r.SupplierBarang?.namaToko || '').toLowerCase().includes(term) ||
      (r.Bantuan?.JenisBantuan || '').toLowerCase().includes(term)
    );
  });

  if (loading) return <Box textAlign="center" py={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const contentStyle = isMobile ? {} : { marginLeft: "250px", marginRight: "20px" };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="table-responsive rounded border">
      <div className="main-content" style={contentStyle}>
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h7" sx={{  display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' } }}>
              <DescriptionIcon color="primary" fontSize="large" />
              File RAB Bantuan
            </Typography>
            {canUpload && (
              <Button 
                variant="contained" 
                startIcon={<UploadFileIcon />} 
                onClick={openUpload}
                sx={{ borderRadius: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Upload RAB
              </Button>
            )}
          </Box>
          
          {/* Search & Info */}
          <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 3, bgcolor: '#f5f5f5' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
              <strong>Informasi Akses:</strong> Admin: semua data (CRUD) | TFL: bantuan terassign (Upload & Edit) | Supplier: Preview saja
            </Typography>
            <TextField
              size="small"
              placeholder="Cari nama penerima, TFL, toko, atau keterangan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ width: { xs: '100%', sm: 400 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Paper>

          {/* Table */}
          {filteredRows.length === 0 ? (
            <Paper sx={{ width: '100%', overflow: 'hidden', p: 4 }}>
              <Box textAlign="center">
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchTerm ? 'Tidak ada data yang sesuai dengan pencarian' : 'Belum ada data RAB'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'Coba gunakan kata kunci lain' : 'Upload RAB pertama Anda dengan klik tombol di atas'}
                </Typography>
              </Box>
            </Paper>
          ) : (
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table 
                  sx={{ 
                    minWidth: isMobile ? 600 : 900,
                    tableLayout: isMobile ? 'auto' : 'fixed',
                    '& .MuiTableCell-root': {
                      padding: isMobile ? '6px 4px' : '10px 14px',
                      fontSize: isMobile ? '0.7rem' : '0.875rem',
                    }
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                      <TableCell sx={{ fontWeight: 'bold', width: '5%' }}>No.</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '25%' : '20%' }}>
                        Nama Penerima
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '15%' : '15%' }}>
                        Jenis Bantuan
                      </TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontWeight: 'bold', width: '18%' }}>
                          Toko / Supplier
                        </TableCell>
                      )}
                      {!isTablet && (
                        <TableCell sx={{ fontWeight: 'bold', width: '10%' }}>
                          TFL
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '12%' : '10%' }}>
                        Status
                      </TableCell>
                      {!isTablet && (
                        <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>
                          File
                        </TableCell>
                      )}
                      <TableCell sx={{ fontWeight: 'bold', width: isMobile ? '20%' : '15%', textAlign: 'center' }}>
                        Aksi
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map((rab, index) => {
                      const b = rab.Bantuan || {};
                      const pemohon = b.namaLengkap || b.pemohon?.nama || `Bantuan #${b.id}`;
                      const filePath = rab.fileRab;
                      const fileName = filePath ? filePath.split('/').pop() : '';
                      const isOwner = isTFL && rab.TFL?.id === userId;
                      
                      return (
                        <TableRow key={rab.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: isMobile ? 24 : 30, height: isMobile ? 24 : 30, bgcolor: 'primary.main' }}>
                                <PersonIcon fontSize={isMobile ? 'small' : 'small'} />
                              </Avatar>
                              <Typography 
                                variant="body2" 
                                fontWeight="medium" 
                                sx={{ 
                                  fontSize: isMobile ? '0.7rem' : '0.875rem',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {pemohon}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={b.JenisBantuan || '-'} 
                              size="small" 
                              variant="outlined"
                              color="primary"
                              sx={{ 
                                fontSize: isMobile ? '0.55rem' : '0.7rem',
                                height: isMobile ? 20 : 24,
                              }}
                            />
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <StoreIcon fontSize="small" color="action" />
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {rab.SupplierBarang?.namaToko || rab.SupplierBarang?.namaSupplier || '-'}
                                </Typography>
                              </Box>
                            </TableCell>
                          )}
                          {!isTablet && (
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                {rab.TFL?.username || '-'}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Chip
                              size="small"
                              label={rab.status || 'draft'}
                              color={
                                rab.status === 'disetujui' ? 'success' : 
                                rab.status === 'diajukan' ? 'warning' : 
                                'default'
                              }
                              sx={{ 
                                minWidth: isMobile ? 60 : 80, 
                                fontSize: isMobile ? '0.55rem' : '0.7rem',
                                height: isMobile ? 20 : 24,
                              }}
                            />
                          </TableCell>
                          {!isTablet && (
                            <TableCell>
                              {filePath ? (
                                <FilePreview 
                                  filePath={filePath} 
                                  fileName={fileName} 
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>
                            <Stack 
                              direction="row" 
                              spacing={isMobile ? 0.3 : 0.5} 
                              justifyContent="center"
                              flexWrap="wrap"
                            >
                              <Tooltip title="Detail">
                                <IconButton 
                                  size="small" 
                                  color="info"
                                  onClick={() => handleViewDetail(rab)}
                                  sx={{ padding: isMobile ? '2px' : '4px' }}
                                >
                                  <VisibilityIcon fontSize={isMobile ? 'small' : 'small'} />
                                </IconButton>
                              </Tooltip>
                              
                              {(isAdmin || (isTFL && isOwner)) && (
                                <Tooltip title="Edit">
                                  <IconButton 
                                    size="small" 
                                    color="warning"
                                    onClick={() => openEdit(rab)}
                                    sx={{ padding: isMobile ? '2px' : '4px' }}
                                  >
                                    <EditIcon fontSize={isMobile ? 'small' : 'small'} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              
                              {isAdmin && (
                                <Tooltip title="Hapus">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDelete(rab.id, pemohon)}
                                    sx={{ padding: isMobile ? '2px' : '4px' }}
                                  >
                                    <DeleteIcon fontSize={isMobile ? 'small' : 'small'} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>

        {/* ============================================================
            UPLOAD / CREATE DIALOG
            ============================================================ */}
        <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'primary.main', color: 'white' }}>
            <UploadFileIcon /> Upload File RAB
          </DialogTitle>
          <DialogContent dividers sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Pilih Bantuan"
                size="small"
                value={uploadForm.BantuanId}
                onChange={handleBantuanChange}
                required
                fullWidth
              >
                <MenuItem value="">— Pilih bantuan —</MenuItem>
                {bantuanOpts.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Pilih Supplier / Toko"
                size="small"
                value={uploadForm.SupplierBarangId || ''}
                onChange={(e) => setUploadForm((p) => ({ ...p, SupplierBarangId: e.target.value }))}
                fullWidth
                disabled={!uploadForm.BantuanId || supplierOpts.length === 0}
                helperText={!uploadForm.BantuanId ? 'Pilih bantuan terlebih dahulu' : 
                            supplierOpts.length === 0 ? 'Tidak ada supplier untuk bantuan ini' : ''}
              >
                <MenuItem value="">— Pilih supplier —</MenuItem>
                {supplierOpts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                label="Keterangan (opsional)"
                size="small"
                multiline
                rows={3}
                value={uploadForm.keterangan}
                onChange={(e) => setUploadForm((p) => ({ ...p, keterangan: e.target.value }))}
                fullWidth
                placeholder="Tambahkan keterangan jika diperlukan..."
              />
              
              <TextField
                select
                label="Status"
                size="small"
                value={uploadForm.status}
                onChange={(e) => setUploadForm((p) => ({ ...p, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="diajukan">Diajukan</MenuItem>
                <MenuItem value="disetujui">Disetujui</MenuItem>
              </TextField>
              
              <Button 
                variant="outlined" 
                component="label" 
                startIcon={<UploadFileIcon />}
                sx={{ py: 2, borderStyle: 'dashed' }}
              >
                {uploadFile ? uploadFile.name : 'Pilih File (PDF / Gambar)'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,image/*"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
              </Button>
              
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  Maksimum 5MB. Format yang didukung: PDF, JPG, PNG, JPEG
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setUploadOpen(false)}>Batal</Button>
            <Button 
              variant="contained" 
              disabled={saving} 
              onClick={handleUploadRab}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {saving ? 'Mengupload...' : 'Simpan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ============================================================
            DETAIL DIALOG
            ============================================================ */}
        <RabDetailDialog 
          open={detailOpen} 
          onClose={() => setDetailOpen(false)} 
          rab={selectedRab}
          userRole={userRole}
          onEdit={openEdit}
        />

        {/* ============================================================
            EDIT DIALOG
            ============================================================ */}
        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'warning.main', color: 'white' }}>
            <EditIcon /> Edit RAB
          </DialogTitle>
          <DialogContent dividers sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                select
                label="Pilih Bantuan"
                size="small"
                value={editForm.BantuanId}
                onChange={handleEditBantuanChange}
                required
                fullWidth
              >
                <MenuItem value="">— Pilih bantuan —</MenuItem>
                {editBantuanOpts.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.label}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                select
                label="Pilih Supplier / Toko"
                size="small"
                value={editForm.SupplierBarangId || ''}
                onChange={(e) => setEditForm((p) => ({ ...p, SupplierBarangId: e.target.value }))}
                fullWidth
                disabled={!editForm.BantuanId || editSupplierOpts.length === 0}
                helperText={!editForm.BantuanId ? 'Pilih bantuan terlebih dahulu' : 
                            editSupplierOpts.length === 0 ? 'Tidak ada supplier untuk bantuan ini' : ''}
              >
                <MenuItem value="">— Pilih supplier —</MenuItem>
                {editSupplierOpts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
                ))}
              </TextField>
              
              <TextField
                label="Keterangan"
                size="small"
                multiline
                rows={3}
                value={editForm.keterangan}
                onChange={(e) => setEditForm((p) => ({ ...p, keterangan: e.target.value }))}
                fullWidth
                placeholder="Tambahkan keterangan jika diperlukan..."
              />
              
              <TextField
                select
                label="Status"
                size="small"
                value={editForm.status}
                onChange={(e) => setEditForm((p) => ({ ...p, status: e.target.value }))}
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="diajukan">Diajukan</MenuItem>
                <MenuItem value="disetujui">Disetujui</MenuItem>
              </TextField>
              
              <Button 
                variant="outlined" 
                component="label" 
                startIcon={<UploadFileIcon />}
                sx={{ py: 2, borderStyle: 'dashed' }}
              >
                {editFile ? editFile.name : 'Ganti File (Opsional)'}
                <input
                  type="file"
                  hidden
                  accept="application/pdf,image/*"
                  onChange={(e) => setEditFile(e.target.files?.[0] || null)}
                />
              </Button>
              
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  Kosongkan jika tidak ingin mengganti file. Maksimum 5MB.
                </Typography>
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setEditOpen(false)}>Batal</Button>
            <Button 
              variant="contained" 
              color="warning"
              disabled={saving} 
              onClick={handleEditSubmit}
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {saving ? 'Menyimpan...' : 'Update'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default RabMenu;